import axios from 'axios';
import { useSnackbar } from 'notistack5';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
import { apiGateway } from 'src/config';
import { NetworkAccessError } from 'src/errors/errors';
import { CalendarInfoEntity } from 'src/features/general/enquete-calendar/hooks/useCalendarLayout';
import { ReminderEntity } from 'src/features/general/enquete-calendar/hooks/useReminderListGrid';
import { useBearerToken } from './useBearerToken';
export interface useCalendarApiEntity {
  id: string;
  // 適用期間
  applicablePeriod: string;
  // 適用開始日
  openAt: string;
  // 適用終了日
  closeAt: string;
  // Sコード
  companyCode?: string;
  // 格付
  rank: string;
  // 会社名（日本語）
  companyNameJpn?: string;
  // 会社名（英語）
  companyNameEng?: string;
  // 会社略称
  companyAbbreviation: string;
  // 本部名（日本語）
  divisionNameJpn?: string;
  // 本部名（英語）
  divisionNameEng?: string;
  // SBU名（日本語）
  sbuNameJpn?: string;
  // SBU名（英語）
  sbuNameEng?: string;
  // 部名（日本語）
  departmentNameJpn?: string;
  // 部名（英語）
  departmentNameEng?: string;
  // 見出し（日本語）
  titleJpn: string;
  // 見出し（英語）
  titleEng: string;
  // 作成者
  createdUserId: string;
  // リマインド
  reminder: string;
  // 作成者(日本語)
  userNameJpn: string;
  // 作成者(英語)
  userNameEng: string;
}

export interface RespondentRequestEntity {
  id: string;
  // アンケート詳細のid
  surveyDetailId?: string;
  // アンケートタイトル（日本語）
  titleEng: string;
  // アンケートタイトル（英語）
  titleJpn: string;
}

export interface CalendarEntity {
  // ID
  id: string;
  // アンケート詳細のid
  surveyDetailId: string;
}

/**
 * カレンダー関連データ取得 API
 *
 * @returns
 */
export const useCalendarApi = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { getBearerToken } = useBearerToken();
  /**
   * アンケートドロップダウンデータ取得
   *
   * @returns
   */
  const getSurveyList = async (): Promise<RespondentRequestEntity[] | undefined> => {
    try {
      const token = await getBearerToken();
      const result = await axios.get<RespondentRequestEntity[] | undefined>(apiGateway + '/enquete-calendar/get-survey-list', {
        headers: {
          Authorization: token,
        },
        timeout: 60000,
      });
      return result.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // エラーメッセージ：アンケートの取得に失敗しました
        enqueueSnackbar(t('enqueteCalendarList.apiExceptionMessage.useCalendarApi.failedGetSurveyData'), { variant: 'error' });
      } else {
        throw error;
      }
    }
  };
  /**
   * 条件付きデータ件数取得
   *
   * @returns
   */
  const getCountLimitedCalendar = async (
    // 選択したアンケートリスト
    surveyIds: string[],
    // 検索条件：年
    year: string,
    // 検索条件：月
    month: string,
    // 検索条件：日
    day: string,
    // フィルター条件
    filterModel: { [key: string]: any },
    // 現在の言語
    currentLang: string,
  ): Promise<number | undefined> => {
    try {
      const token = await getBearerToken();
      const result = await axios.get<number>(apiGateway + '/enquete-calendar/count-calendar-list', {
        headers: {
          Authorization: token,
        },
        params: {
          // 選択したアンケートリスト
          surveyIds: surveyIds,
          // 検索条件：年
          year: year,
          // 検索条件：月
          month: month,
          // 検索条件：日
          day: day,
          // フィルター条件
          FilterModel: filterModel,
          // フィルター条件
          CurrentLang: currentLang,
        },
        timeout: 60000,
      });
      return result.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // エラーメッセージ：カレンダー情報の取得に失敗しました
        enqueueSnackbar(t('enqueteCalendarList.apiExceptionMessage.useCalendarApi.failedGetData'), { variant: 'error' });
      } else {
        throw error;
      }
    }
  };

  /**
   * 条件付きデータ取得
   *
   * @returns
   */
  const getLimitedList = async (
    // 選択したアンケートリスト
    surveyIds: string[],
    // 検索条件：年
    year: string,
    // 検索条件：月
    month: string,
    // 検索条件：日
    day: string,
    // 毎ページ件数
    blockSize: number,
    // 開始行
    startRow: number,
    // ソート条件
    sortModel: any,
    // フィルター条件
    filterModel: { [key: string]: any },
    // 現在の言語
    currentLang: string,
  ): Promise<useCalendarApiEntity[] | undefined> => {
    try {
      const token = await getBearerToken();
      const result = await axios.get<useCalendarApiEntity[] | undefined>(apiGateway + '/enquete-calendar/get-calendar-list', {
        headers: {
          Authorization: token,
        },
        params: {
          // 選択したアンケートリスト
          surveyIds: surveyIds,
          // 検索条件：年
          year: year,
          // 検索条件：月
          month: month,
          // 検索条件：日
          day: day,
          // 毎ページ件数
          BlockSize: blockSize,
          // 開始行
          StartRow: startRow,
          // ソート条件
          SortModel: sortModel,
          // フィルター条件
          FilterModel: filterModel,
          // 現在の言語
          CurrentLang: currentLang,
        },
        timeout: 60000,
      });
      return result.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // エラーメッセージ：カレンダー情報の取得に失敗しました
        enqueueSnackbar(t('enqueteCalendarList.apiExceptionMessage.useCalendarApi.failedGetData'), { variant: 'error' });
      } else {
        throw error;
      }
    }
  };

  /**
   * カレンダー明細データ削除
   *
   * @param calendarIds
   * @returns
   */
  const deleteCalendarData = async (calendarIds: string[]) => {
    try {
      const token = await getBearerToken();
      const result = await axios.post<CalendarEntity | undefined>(apiGateway + '/enquete-calendar/delete-calendar', calendarIds, {
        headers: {
          Authorization: token,
        },
        timeout: 60000,
      });
      if (result === undefined) return;
      return result.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new NetworkAccessError(error.response?.status);
      }
      throw error;
    }
  };

  /**
   * カレンダー編集データ取得
   *
   * @param calendarIds
   * @returns
   */
  const getCalendarData = async (calendarlId: string) => {
    try {
      const token = await getBearerToken();
      const result = await axios.get<CalendarInfoEntity | undefined>(apiGateway + '/enquete-calendar/calendar-edit/' + calendarlId, {
        headers: {
          Authorization: token,
        },
        timeout: 60000,
      });
      if (result === undefined) return;
      return result.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // エラーメッセージ：カレンダー情報の取得に失敗しました
        enqueueSnackbar(t('enqueteCalendarList.apiExceptionMessage.useCalendarApi.failedGetData'), { variant: 'error' });
      }
      throw error;
    }
  };

  /**
   * リマインド状況取得
   *
   * @returns
   */
  const getReminderList = async (calendarlId: string): Promise<ReminderEntity[] | undefined> => {
    try {
      const token = await getBearerToken();
      const result = await axios.get<ReminderEntity[] | undefined>(apiGateway + '/enquete-calendar/reminder-list/' + calendarlId, {
        headers: {
          Authorization: token,
        },
        timeout: 60000,
      });
      return result.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // エラーメッセージ：リマインド情報の取得に失敗しました
        enqueueSnackbar(t('enqueteCalendarList.apiExceptionMessage.useReminderApi.failedGetData'), { variant: 'error' });
      } else {
        throw error;
      }
    }
  };

  /**
   * カレンダーデータ保存
   *
   * @param calendarData カレンダーデータ
   * @param reminderListData リマインド明細データ
   * @returns
   */
  const saveCalendarData = async (calendarData: CalendarInfoEntity, reminderListData: ReminderEntity[]) => {
    try {
      const token = await getBearerToken();
      if (calendarData.id !== undefined && calendarData.id !== '') {
        const result = await axios.put<CalendarEntity | undefined>(
          apiGateway + '/enquete-calendar/' + calendarData.id,
          {
            // カレンダーデータ
            calendarData: calendarData,
            // リマインド明細データ
            reminderListData: reminderListData,
          },
          {
            headers: {
              Authorization: token,
            },
            timeout: 60000,
          },
        );
        if (result === undefined) return;
      } else {
        const result = await axios.post<CalendarEntity | undefined>(
          apiGateway + '/enquete-calendar/create',
          {
            // カレンダーデータ
            calendarData: calendarData,
            // リマインド明細データ
            reminderListData: reminderListData,
          },
          {
            headers: {
              Authorization: token,
            },
            timeout: 60000,
          },
        );
        if (result === undefined) return;
      }
      // 成功メッセージ：更新しました
      enqueueSnackbar(t('message.update'), { variant: 'success' });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new NetworkAccessError(error.response?.status);
      }
      throw error;
    }
  };

  const getConfirmData = async (respondentTargetId: string): Promise<RespondentRequestEntity | undefined> => {
    try {
      const token = await getBearerToken();
      const result = await axios.get<RespondentRequestEntity | undefined>(apiGateway + '/enquete-calendar/confirm-request/' + respondentTargetId, {
        headers: {
          Authorization: token,
        },
        timeout: 60000,
      });
      return result.data;
    } catch (error) {
      throw error;
    }
  };

  return {
    // アンケートドロップダウンデータ取得
    getSurveyList,
    // カレンダーデータ件数取得
    getCountLimitedCalendar,
    // カレンダーデータ取得
    getLimitedList,
    // カレンダー編集データ取得
    getCalendarData,
    // カレンダー明細データ削除
    deleteCalendarData,
    // リマインド状況取得
    getReminderList,
    // カレンダーデータ保存
    saveCalendarData,
    // 確認者情報の取得
    getConfirmData,
  };
};
